import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import api from '../../services';
import { fetchAll } from '../../services/helpers';

// Type definitions
interface Site {
  id: number;
  layoutId: number;
  domain: string;
  description: string;
  status: number;
}

interface Domain {
  id: number;
  name: string;
  isActive: boolean;
  gtmId: string;
  isRedirectDomain: boolean;
  tags: any[];
  customVariables: any;
  site: Site;
  domainVertical: any;
  defaultLandingPageGroupOverride: any;
}

interface OfferBlock {
  id?: number;
  active: boolean;
  name: string;
  offer_block_checkout_status: boolean;
  offerBlockOffers: any[];
  offersPublished: {
    offer_block: any[];
  };
  published: boolean;
  zuulUser: any;
  offer?: {
    criteriaUserAttributes: any;
    suppressionRules: any;
    criteriaDayHourRestrictions: any;
  };
}

interface DayHourRestriction {
  restrictionDay: string;
  [key: string]: any;
}

// Custom hooks
export const useOfferBlocks = () =>
  useQuery({
    queryKey: ['offerBlocks'],
    queryFn: () => fetchAll('offerblocks'),
  });

const fetchOfferBlock = async (id: string | number): Promise<OfferBlock> => {
  if (id === 'create') {
    return {
      active: true,
      name: '',
      offer_block_checkout_status: false,
      offerBlockOffers: [],
      offersPublished: {
        offer_block: [],
      },
      published: true,
      zuulUser: JSON.parse(localStorage.getItem('userData') || '{}'),
    };
  }
  return api.get(`offerblocks/${id}`).then((res) => res.data);
};

function parseDayHourRestriction(obj: Record<string, any>): DayHourRestriction[] {
  return Object.entries(obj).map(([restrictionDay, properties]) => ({
    restrictionDay,
    ...properties,
  }));
}

export const useOfferBlock = (id: string | number) =>
  useQuery({
    queryKey: ['offerBlocks', id],
    queryFn: () => fetchOfferBlock(id),
    select: (data: OfferBlock) => {
      if (data.offer) {
        const processedData = { ...data };
        processedData.offer = { ...data.offer };
        processedData.offer.criteriaUserAttributes = JSON.parse(data.offer.criteriaUserAttributes);
        processedData.offer.suppressionRules = JSON.parse(data.offer.suppressionRules);
        processedData.offer.criteriaDayHourRestrictions = data.offer.criteriaDayHourRestrictions
          ? parseDayHourRestriction(JSON.parse(data.offer.criteriaDayHourRestrictions))
          : [];
        return processedData;
      }
      // ZET-4976: always hand back a copy. OfferBlockDetails mutates what it gets (e.g. adds `offer`), and
      // returning the cached object let that leak into the cache and break select() on the next Create visit.
      return { ...data };
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

export const useOfferBlockByOfferId = (enabled: boolean, offerId: number) =>
  useQuery({
    queryKey: ['offerBlockOfferId', offerId],
    queryFn: () => api.get(`offerblocks/offer/${offerId}`).then((res) => res.data),
    enabled,
  });

export const useCreateOfferBlock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OfferBlock) => api.post('offerblocks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offerBlocks'] });
    },
  });
};

export const useUpdateOfferBlock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OfferBlock & { id: number }) => api.put(`offerblocks/${data.id}`, data),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['offerBlocks', data.data.id.toString()] });
      queryClient.invalidateQueries({ queryKey: ['offerBlocks'] });
      queryClient.invalidateQueries({ queryKey: ['offersTable'] });
    },
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any & { id: number }) => api.put(`offers/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offersTable'] });
    },
  });
};

export const useLayoutsTable = <T = any>(select?: (data: any) => T) =>
  useQuery({
    queryKey: ['layoutsTable'],
    queryFn: () => api.get('layouts/table').then((res) => res.data),
    ...(select && { select }),
  });

export const useContainersTable = <T = any>(select?: (data: any) => T) =>
  useQuery({
    queryKey: ['containersTable'],
    queryFn: () => api.get('containers/table').then((res) => res.data),
    ...(select && { select }),
  });

export const useDomains = <T = Domain[]>(enabled: boolean = true, selectFn?: (data: Domain[]) => T) =>
  useQuery({
    queryKey: ['domains'],
    queryFn: (): Promise<Domain[]> => api.get('domains').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useDevices = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get('devices').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useAibList = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['aibList'],
    queryFn: () => api.get('aibList').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useOperatingSystems = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['operatingSystems'],
    queryFn: () => api.get('operatingsystems').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useBrowsers = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['browsers'],
    queryFn: () => api.get('browsers').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useUserAttributes = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['userAttributes'],
    queryFn: () => api.get('user-attribute').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useAffiliates = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['affiliates'],
    queryFn: () => api.get('affiliates').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useHandlers = <T = any>(enabled: boolean, selectFn?: (data: any) => T) =>
  useQuery({
    queryKey: ['handlers'],
    queryFn: () => api.get('handlers').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });
